import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, CreditCard, Sparkles, TrendingUp } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { type OrgWithPlan } from "@/lib/orgBilling";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { trackStarterUpgrade, trackGrowthUpgrade } from "@/lib/tracking/conversion";

interface BillingWidgetProps {
    org: OrgWithPlan;
    usage?: {
        activeContests: number;
        totalEntries?: number; // Reserved for future entries-based limits
    };
}

export function BillingWidget({ org, usage }: BillingWidgetProps) {
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const { toast } = useToast();

    const handleCheckout = async (planKey: 'starter' | 'growth') => {
        setIsLoading(true);
        // Store path for robust return handling
        const currentPath = window.location.pathname + window.location.search;
        sessionStorage.setItem("rk_return_to", currentPath);

        try {
            const { data, error } = await supabase.functions.invoke('billing-checkout', {
                body: {
                    planKey,
                    returnUrl: window.location.href
                }
            });
            if (error) throw error;
            if (data?.upgraded) {
                // Fire upgrade event
                if (planKey === 'starter') {
                    trackStarterUpgrade(false);
                } else if (planKey === 'growth') {
                    trackGrowthUpgrade();
                }

                toast({
                    title: "Plan upgraded!",
                    description: "Welcome. Your plan is now active.",
                });
                setTimeout(() => {
                    window.location.reload();
                }, 1500);
                return;
            }
            if (data?.url) {
                localStorage.setItem('rk_pending_upgrade', planKey);
                window.location.href = data.url;
            }
        } catch (err) {
            console.error(err);
            alert("Failed to start checkout");
        } finally {
            setIsLoading(false);
        }
    };

    // Limits Logic
    const isFree = org.plan_key === 'free';
    const isStarter = org.plan_key === 'starter';
    const isGrowth = org.plan_key === 'growth';

    // Free limit: 1 active contest. Starter: 3. Growth: Unlimited.
    // User requested "Free: show Entries used / 500" if possible. 
    // If usage.totalEntries is passed, we use it. Otherwise fallback to contests.
    // For now we assume usage.totalEntries is passed.

    let limitLabel = "";
    let progressValue = 0;

    if (isFree) {
        // Free: 1 active contest max
        const activeLimit = 1;
        const activeUsed = usage?.activeContests || 0;
        progressValue = Math.min((activeUsed / activeLimit) * 100, 100);
        limitLabel = `${activeUsed} / ${activeLimit} Active Contest`;
    } else if (isStarter) {
        const activeLimit = 3;
        const activeUsed = usage?.activeContests || 0;
        progressValue = Math.min((activeUsed / activeLimit) * 100, 100);
        limitLabel = `${activeUsed} / ${activeLimit} Active Contests`;
    } else {
        // Growth
        limitLabel = "Unlimited Usage";
        progressValue = 0; // or 100? Hidden generally.
    }

    return (
        <div className="bg-card border border-border rounded-xl p-6 mb-8 shadow-sm space-y-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <h3 className="font-heading font-bold text-lg flex items-center gap-2">
                        Current Plan:
                        <Badge variant={isFree ? "secondary" : "default"} className="uppercase tracking-wider">
                            {org.plan_key}
                        </Badge>
                    </h3>
                    <p className="text-muted-foreground text-sm mt-1">
                        {isFree && "Limited features. Upgrade to unlock more."}
                        {isStarter && "Starter Plan (3 Active Contests)."}
                        {isGrowth && "Growth Plan (Unlimited Contests)."}
                    </p>
                </div>
                <div className="flex flex-wrap gap-3">
                    {isFree && (
                        <>
                            <Button onClick={() => handleCheckout('starter')} disabled={isLoading} variant="outline" className="border-primary/20 hover:bg-primary/5">
                                {isLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2 text-primary" />}
                                Get Starter ($9/mo)
                            </Button>
                            <Button onClick={() => handleCheckout('growth')} disabled={isLoading} className="bg-gradient-primary hover:shadow-lg transition-all">
                                {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                Get Growth ($29/mo)
                            </Button>
                        </>
                    )}
                    {!isFree && (
                        <Button onClick={() => navigate("/admin/settings?tab=billing")} variant="outline">
                            <CreditCard className="w-4 h-4 mr-2" />
                            Manage Billing
                        </Button>
                    )}
                </div>
            </div>

            {/* Progress Bar */}
            {!isGrowth && (
                <div className="space-y-2">
                    <div className="flex justify-between text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        <span>Plan Usage</span>
                        <span>{limitLabel}</span>
                    </div>
                    <Progress value={progressValue} className="h-2 bg-secondary" />
                </div>
            )}
        </div>
    )
}
